import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Bell,
  BookOpen,
  CalendarRange,
  Compass,
  Cpu,
  FileBarChart2,
  FileText,
  FlaskConical,
  GitCompareArrows,
  HelpCircle,
  Layers,
  LineChart,
  Rocket,
  Search,
  SearchX,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Markdown } from "@/components/ui/markdown";
import {
  DEFAULT_HELP_SECTION_ID,
  HELP_GROUPS,
  HELP_SECTIONS,
  findHelpSection,
  type HelpSection,
} from "./helpContent";

const ICONS: Record<string, LucideIcon> = {
  Sparkles,
  Rocket,
  BookOpen,
  SlidersHorizontal,
  Activity,
  LineChart,
  Compass,
  FlaskConical,
  FileBarChart2,
  CalendarRange,
  Bell,
  Layers,
  GitCompareArrows,
  Cpu,
  ShieldCheck,
  Wrench,
  HelpCircle,
  Settings,
  FileText,
};

function iconFor(name: string): LucideIcon {
  return ICONS[name] ?? FileText;
}

function matches(section: HelpSection, query: string): boolean {
  const q = query.toLowerCase();
  return (
    section.title.toLowerCase().includes(q) ||
    section.summary.toLowerCase().includes(q) ||
    section.content.toLowerCase().includes(q)
  );
}

export function HelpView() {
  const { sectionId } = useParams<{ sectionId?: string }>();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const topRef = useRef<HTMLDivElement>(null);

  const activeId = sectionId ?? DEFAULT_HELP_SECTION_ID;
  const section = findHelpSection(activeId);
  const currentIndex = HELP_SECTIONS.findIndex((s) => s.id === activeId);
  const prev = currentIndex > 0 ? HELP_SECTIONS[currentIndex - 1] : undefined;
  const next =
    currentIndex >= 0 && currentIndex < HELP_SECTIONS.length - 1
      ? HELP_SECTIONS[currentIndex + 1]
      : undefined;

  // Scroll back to the top of the reader whenever the section changes.
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [activeId]);

  const filtered = useMemo(
    () => (query.trim() ? HELP_SECTIONS.filter((s) => matches(s, query.trim())) : HELP_SECTIONS),
    [query]
  );

  const go = (id: string) => navigate(`/app/help/${id}`);

  // Links inside the markdown point at in-app routes (/app/...). Intercept plain
  // left-clicks and route them through React Router instead of a full page load.
  // External links (http...) are left alone and open in a new tab.
  const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    const anchor = (e.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (href && href.startsWith("/app/")) {
      e.preventDefault();
      navigate(href);
    }
  };

  const Icon = section ? iconFor(section.icon) : HelpCircle;

  return (
    <div ref={topRef} className="grid grid-cols-1 lg:grid-cols-[248px_1fr] gap-8 animate-fade-up">
      {/* Table of contents */}
      <nav className="lg:sticky lg:top-0 self-start space-y-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help…"
            className="pl-9"
            aria-label="Search help"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="px-1 py-2 text-xs text-slate-500">
            No topics match “{query.trim()}”.
          </p>
        ) : (
          HELP_GROUPS.map((group) => {
            const items = filtered.filter((s) => s.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group}>
                <p className="px-3 mb-1 text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">
                  {group}
                </p>
                <div className="space-y-0.5">
                  {items.map((s) => {
                    const ItemIcon = iconFor(s.icon);
                    const isActive = s.id === activeId;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => go(s.id)}
                        className={cn(
                          "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] text-left transition-colors",
                          isActive
                            ? "bg-brand-50 text-brand-700 font-semibold"
                            : "text-slate-600 hover:text-brand-950 hover:bg-slate-50"
                        )}
                      >
                        <ItemIcon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{s.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </nav>

      {/* Content */}
      <div className="min-w-0">
        {!section ? (
          <EmptyState
            icon={SearchX}
            title="Help page not found"
            description="That documentation page doesn't exist. Head back to the overview to find your way."
            action={<Button onClick={() => go(DEFAULT_HELP_SECTION_ID)}>Go to overview</Button>}
          />
        ) : (
          <article>
            <header className="flex items-start gap-3 pb-5 mb-6 border-b border-slate-100">
              <div className="mt-0.5 w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-brand-600" />
              </div>
              <div>
                <p className="text-[11px] font-semibold tracking-[0.08em] uppercase text-brand-500">
                  {section.group}
                </p>
                <h2 className="text-xl font-bold text-brand-950 tracking-tight leading-tight">
                  {section.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{section.summary}</p>
              </div>
            </header>

            <div onClickCapture={handleContentClick}>
              <Markdown
                content={section.content}
                className="text-sm text-slate-700 [&_h2]:mt-8 [&_h3]:mt-6 [&_table]:text-[13px] [&_hr]:my-8 [&_hr]:border-slate-200"
              />
            </div>

            {(prev || next) && (
              <nav className="mt-10 pt-5 border-t border-slate-100 flex items-stretch justify-between gap-3">
                {prev ? (
                  <button
                    type="button"
                    onClick={() => go(prev.id)}
                    className="group flex-1 text-left rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-200 hover:bg-slate-50 transition-colors"
                  >
                    <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Previous
                    </span>
                    <span className="mt-0.5 block text-sm font-semibold text-brand-950">
                      {prev.title}
                    </span>
                  </button>
                ) : (
                  <span className="flex-1" />
                )}
                {next ? (
                  <button
                    type="button"
                    onClick={() => go(next.id)}
                    className="group flex-1 text-right rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-200 hover:bg-slate-50 transition-colors"
                  >
                    <span className="flex items-center justify-end gap-1.5 text-[11px] font-medium text-slate-400">
                      Next
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                    <span className="mt-0.5 block text-sm font-semibold text-brand-950">
                      {next.title}
                    </span>
                  </button>
                ) : (
                  <span className="flex-1" />
                )}
              </nav>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
